/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdVaultLock — vault unlock UI. Shows a lock icon and a side-drawer
 * with 6 word inputs. Calls `vaultService.unlockVault` / `vaultService.lock`
 * and listens for the `passphrase-recovered` event from `TdForgotPass`
 * (#190) so a successful recovery round-trip can drive an automated
 * unlock through the same code path as manual entry.
 *
 * Lit light-DOM component. The structural template lives in `render()`;
 * the unlock button's transient text/variant/disabled state stays
 * imperative because it includes a 2-second failure-reset timeout that
 * doesn't fit cleanly into reactive state — and the existing test suite
 * asserts those properties via direct DOM reads after a `setTimeout(0)`
 * settle, which would need re-architecting if we moved them under `@state`.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { vaultService } from '../services/vault.js';
import { t, LocaleController } from '../services/i18n.js';

/** Detail payload of the `passphrase-recovered` event dispatched on `document` by TdForgotPass (#190). */
interface PassphraseRecoveredDetail {
  passphrase: string;
}

/** Detail payload of the bubble-up `vault-unlock-failed` / `vault-lock-failed` events. */
interface VaultErrorDetail {
  error: string;
}

interface WaDrawerElement extends HTMLElement {
  open: boolean;
}

interface WaButtonElement extends HTMLElement {
  disabled: boolean;
  variant: string;
}

interface WaInputElement extends HTMLElement {
  value: string;
}

@customElement('td-vaultlock')
export class TdVaultLock extends LitElement {
  @state() private isLocked = true;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  private _passphraseRecoveredListener: ((event: Event) => void) | null = null;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.checkVaultStatus();

    // #190: the recovery wizard (TdForgotPass) dispatches `passphrase-recovered`
    // on `document` after a successful round-trip. Catch it here, fill the 6
    // word inputs, and trigger the unlock — symmetric with the manual flow.
    this._passphraseRecoveredListener = (event: Event) => {
      const detail = (event as CustomEvent<PassphraseRecoveredDetail>).detail;
      const passphrase = detail?.passphrase;
      if (typeof passphrase === 'string' && passphrase.length > 0) {
        void this.fillAndUnlock(passphrase);
      }
    };
    document.addEventListener(
      'passphrase-recovered',
      this._passphraseRecoveredListener
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._passphraseRecoveredListener) {
      document.removeEventListener(
        'passphrase-recovered',
        this._passphraseRecoveredListener
      );
      this._passphraseRecoveredListener = null;
    }
  }

  async checkVaultStatus(): Promise<void> {
    try {
      const isReady = await vaultService.isVaultReady();
      this.isLocked = !isReady;
      this.updateDisplay();
    } catch (error) {
      console.error('Error checking vault status:', error);
      this.isLocked = true;
      this.updateDisplay();
    }
  }

  /** Close the drawer; the icon-name reflects via `@state` → `render()`. */
  updateDisplay(): void {
    const drawer = this.querySelector<WaDrawerElement>('.passphrase-drawer');
    if (drawer) {
      drawer.open = false;
    }
  }

  /**
   * Fill the 6 word inputs from a single space-separated passphrase string
   * and trigger the unlock flow. Public so the recovery wizard can drive an
   * automated unlock after a successful round-trip — same code path as the
   * manual entry, so unlock failure UX is consistent.
   *
   * @param passphrase - space-separated 6-word passphrase
   */
  async fillAndUnlock(passphrase: string): Promise<void> {
    if (!passphrase || typeof passphrase !== 'string') return;
    const words = passphrase.trim().split(/\s+/);
    const fields = this.querySelectorAll<WaInputElement>('.passphrase-field');
    fields.forEach((field, index) => {
      field.value = words[index] ?? '';
    });
    this.updateUnlockButton();
    await this.handleUnlock();
  }

  private _handleDemo(): void {
    const demoPassphrase = ['one', 'two', 'three', 'four', 'five', 'six'];
    const fields = this.querySelectorAll<WaInputElement>('.passphrase-field');
    fields.forEach((field, index) => {
      field.value = demoPassphrase[index] ?? '';
    });
    this.updateUnlockButton();
  }

  private _handleFieldInput(): void {
    this.updateUnlockButton();
  }

  private _handleKeyDown(event: KeyboardEvent, index: number): void {
    const fields = this.querySelectorAll<WaInputElement>('.passphrase-field');
    const current = fields[index];
    if (
      event.key === 'Backspace' &&
      current &&
      current.value === '' &&
      index > 0
    ) {
      fields[index - 1]?.focus();
    }

    if (event.key === 'Enter') {
      void this.handleUnlock();
    }
  }

  private _handleForgotClick(): void {
    // #190: "Forgot passphrase?" opens the recovery wizard via a document-
    // level event. TdForgotPass listens for `forgot-passphrase` and shows
    // its <wa-dialog>. This component stays decoupled from the wizard —
    // the contract is just the event name + the symmetric `passphrase-
    // recovered` event the wizard dispatches when the user confirms.
    document.dispatchEvent(new CustomEvent('forgot-passphrase'));
  }

  async handleLockIconClick(): Promise<void> {
    if (this.isLocked) {
      const passphraseDrawer =
        this.querySelector<WaDrawerElement>('.passphrase-drawer');
      if (passphraseDrawer) {
        passphraseDrawer.open = true;
      }
    } else {
      try {
        await vaultService.lock();
        this.isLocked = true;
        this.updateDisplay();
        // `vault-locked` is dispatched on `document` by `vaultService.lock()`
        // itself (single source of truth — #111). Listeners (TdAccount,
        // TdStoryAction) bind there.
      } catch (error) {
        console.error('Failed to lock vault:', error);

        // Component-only error signal — the service has no symmetric event.
        this.dispatchEvent(
          new CustomEvent<VaultErrorDetail>('vault-lock-failed', {
            detail: { error: (error as Error).message },
            bubbles: true,
          })
        );
      }
    }
  }

  updateUnlockButton(): void {
    const unlockBtn = this.querySelector<WaButtonElement>('.unlock-button');
    if (!unlockBtn) return;

    const fields = this.querySelectorAll<WaInputElement>('.passphrase-field');
    if (fields.length === 0) return;

    const allFieldsFilled = Array.from(fields).every(
      field => field.value && field.value.length > 0
    );
    unlockBtn.disabled = !allFieldsFilled;
  }

  async handleUnlock(): Promise<void> {
    const unlockBtn = this.querySelector<WaButtonElement>('.unlock-button');
    if (!unlockBtn || unlockBtn.disabled) return;

    const fields = this.querySelectorAll<WaInputElement>('.passphrase-field');
    if (fields.length === 0) return;

    const passphrase = Array.from(fields)
      .map(field => field.value || '')
      .join(' ');

    if (!passphrase) return;

    unlockBtn.disabled = true;
    unlockBtn.textContent = t('vaultlock.unlocking');

    try {
      await vaultService.unlockVault(passphrase);
      this.isLocked = false;
      this.updateDisplay();

      fields.forEach(field => {
        field.value = '';
      });
      // `vault-unlocked` is dispatched on `document` by
      // `vaultService.unlockVault()` itself (single source of truth — #111).
      // Listeners (TdAccount, TdStoryAction) bind there.
    } catch (error) {
      console.error('Failed to unlock vault:', error);

      unlockBtn.textContent = t('vaultlock.failedRetry');
      unlockBtn.variant = 'danger';

      fields.forEach(field => {
        field.value = '';
      });

      setTimeout(() => {
        unlockBtn.textContent = t('vaultlock.unlock');
        unlockBtn.variant = 'primary';
        this.updateUnlockButton();
        const first = fields[0];
        if (first) {
          first.focus();
        }
      }, 2000);

      this.dispatchEvent(
        new CustomEvent<VaultErrorDetail>('vault-unlock-failed', {
          detail: { error: (error as Error).message },
          bubbles: true,
        })
      );
    }
  }

  async refreshStatus(): Promise<void> {
    await this.checkVaultStatus();
  }

  /**
   * Seed the unlock button's label after the first render (#89). The
   * label lives outside Lit's managed parts (see the template comment)
   * because `handleUnlock` mutates `unlockBtn.textContent` for its
   * transient Unlocking/Failed states; it resolves in the UI locale
   * active at mount.
   */
  override firstUpdated(): void {
    const unlockBtn = this.querySelector<WaButtonElement>('.unlock-button');
    if (unlockBtn) {
      unlockBtn.append(t('vaultlock.unlock'));
    }
  }

  override render(): TemplateResult {
    const iconName = this.isLocked ? 'lock' : 'lock-open';
    return html`
      <wa-card class="vault-lock-card">
        <!-- The lock toggle is a real button (#243): focusable and
             keyboard-actionable instead of a click-only icon. The
             labelled icon supplies the button's accessible name and
             flips with the vault state. -->
        <wa-button
          class="lock-toggle"
          appearance="plain"
          @click=${() => void this.handleLockIconClick()}
        >
          <wa-icon
            .name=${iconName}
            class="lock-icon"
            label=${
              this.isLocked
                ? t('vaultlock.unlockVault')
                : t('vaultlock.lockVault')
            }
          ></wa-icon>
        </wa-button>
      </wa-card>

      <wa-drawer
        label=${t('vaultlock.drawerLabel')}
        class="passphrase-drawer"
        placement="end"
        open="false"
      >
        <div class="passphrase-fields">
          ${[0, 1, 2, 3, 4, 5].map(
            i => html`
              <wa-input
                label=${t('vaultlock.wordLabel', { n: i + 1 })}
                type="password"
                placeholder=${t('vaultlock.wordPlaceholder', { n: i + 1 })}
                class="passphrase-field"
                data-index=${i}
                password-toggle
                @input=${() => this._handleFieldInput()}
                @keydown=${(e: KeyboardEvent) => this._handleKeyDown(e, i)}
              ></wa-input>
            `
          )}
        </div>

        <div class="unlock-actions" slot="footer">
          <!-- #89: the unlock label is NOT a Lit binding — the transient
               state machine writes unlockBtn.textContent, which would
               eject a binding's marker nodes and make the next render
               throw. firstUpdated seeds the label through the same
               imperative channel instead. -->
          <wa-button
            variant="primary"
            class="unlock-button"
            disabled
            @click=${() => void this.handleUnlock()}
          >
            <wa-icon name="unlock" slot="start"></wa-icon>
          </wa-button>
          <wa-button
            slot="footer"
            appearance="filled outlined"
            variant="danger"
            class="demo-button"
            @click=${() => this._handleDemo()}
          >
            <wa-icon name="face-smile-wink" slot="start"></wa-icon>
            ${t('common.demo')}
          </wa-button>
          <wa-button
            appearance="plain"
            variant="neutral"
            class="forgot-passphrase-link"
            title=${t('vaultlock.forgotTitle')}
            @click=${() => this._handleForgotClick()}
          >
            ${t('vaultlock.forgot')}
          </wa-button>
        </div>
      </wa-drawer>
    `;
  }
}

export default TdVaultLock;
